/**
 * Centralized logging utility for Better TDL
 *
 * Provides consistent logging across the application with:
 * - Severity levels (debug, info, warn, error)
 * - Environment-aware logging (disabled in production for non-errors)
 * - Structured log format with timestamps
 * - Optional context/metadata support
 *
 * @example
 * ```typescript
 * import { logger } from './utils/logger';
 *
 * logger.info('User action', { action: 'save', filename: 'todo.json' });
 * logger.error('Failed to load file', error);
 * logger.warn('Storage quota warning', { usedMB: 9.5, limitMB: 10 });
 * ```
 */

/**
 * Log severity levels
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Log entry metadata
 */
interface LogContext {
  [key: string]: unknown;
}

/**
 * Configuration for the logger
 */
interface LoggerConfig {
  /** Minimum level to log (logs below this level are ignored) */
  minLevel: LogLevel;
  /** Whether to include timestamps in logs */
  includeTimestamp: boolean;
  /** Prefix for all log messages */
  prefix: string;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Check if we're in a production environment
 */
const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};

/**
 * Check if we're in a test environment
 */
const isTest = (): boolean => {
  return process.env.NODE_ENV === 'test';
};

/**
 * Default configuration
 * - In production: only log warnings and errors
 * - In development: log everything
 * - In test: suppress all logs
 */
const getDefaultConfig = (): LoggerConfig => {
  if (isTest()) {
    return {
      minLevel: 'error', // Only errors in tests (and even those are suppressed)
      includeTimestamp: false,
      prefix: '[Better-TDL]',
    };
  }

  if (isProduction()) {
    return {
      minLevel: 'warn',
      includeTimestamp: true,
      prefix: '[Better-TDL]',
    };
  }

  return {
    minLevel: 'debug',
    includeTimestamp: true,
    prefix: '[Better-TDL]',
  };
};

let config: LoggerConfig = getDefaultConfig();

/**
 * Format a log message with optional context
 */
const formatMessage = (level: LogLevel, message: string, context?: LogContext): string => {
  const parts: string[] = [];

  if (config.includeTimestamp) {
    parts.push(`[${new Date().toISOString()}]`);
  }

  parts.push(config.prefix);
  parts.push(`[${level.toUpperCase()}]`);
  parts.push(message);

  return parts.join(' ');
};

/**
 * Check if a log level should be logged based on current config
 */
const shouldLog = (level: LogLevel): boolean => {
  // Suppress all logs in test environment
  if (isTest()) {
    return false;
  }

  return LOG_LEVELS[level] >= LOG_LEVELS[config.minLevel];
};

/**
 * Logger interface for centralized logging
 */
export const logger = {
  /**
   * Log a debug message (development only)
   */
  debug(message: string, context?: LogContext): void {
    if (!shouldLog('debug')) return;
    const formattedMessage = formatMessage('debug', message, context);
    if (context) {
      console.log(formattedMessage, context);
    } else {
      console.log(formattedMessage);
    }
  },

  /**
   * Log an info message
   */
  info(message: string, context?: LogContext): void {
    if (!shouldLog('info')) return;
    const formattedMessage = formatMessage('info', message, context);
    if (context) {
      console.log(formattedMessage, context);
    } else {
      console.log(formattedMessage);
    }
  },

  /**
   * Log a warning message
   */
  warn(message: string, context?: LogContext): void {
    if (!shouldLog('warn')) return;
    const formattedMessage = formatMessage('warn', message, context);
    if (context) {
      console.warn(formattedMessage, context);
    } else {
      console.warn(formattedMessage);
    }
  },

  /**
   * Log an error message
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (!shouldLog('error')) return;
    const formattedMessage = formatMessage('error', message, context);

    if (error && context) {
      console.error(formattedMessage, error, context);
    } else if (error) {
      console.error(formattedMessage, error);
    } else if (context) {
      console.error(formattedMessage, context);
    } else {
      console.error(formattedMessage);
    }
  },

  /**
   * Configure the logger
   * @param newConfig Partial configuration to merge with existing
   */
  configure(newConfig: Partial<LoggerConfig>): void {
    config = { ...config, ...newConfig };
  },

  /**
   * Reset logger to default configuration
   */
  reset(): void {
    config = getDefaultConfig();
  },

  /**
   * Get current configuration (for debugging)
   */
  getConfig(): LoggerConfig {
    return { ...config };
  },
};

export default logger;
